// planIntent — capture & consume the marketing-site plan choice across signup.
//
// The marketing pricing page links to `/dashboard/signup?plan=BASE&billing=annual`
// (PE-1637 contract: UPPERCASE tier enum; optional lowercase monthly|annual). Clerk's
// sign-in-or-up flow redirects a newly created user to /onboarding WITHOUT the query
// string, so the param is gone by the time OrganizationSetup mounts. We therefore
// capture it at the public auth landing (before Clerk redirects) into a dedicated
// localStorage key, and consume it when building the create-org payload.
//
// Why a dedicated key (not the onboarding draft): the draft is scoped per Clerk
// user id, which does not exist yet at the signup landing (user not signed in).
// A standalone key sidesteps that keying race and is trivially consume-once.
//
// The intent flows: ?plan= → capturePlanIntentFromUrl() → localStorage →
// readPlanIntent() in buildCreateOrgPayload → POST /onboarding/create-org
// `selectedPlan` → accounts-backend create_org normalize_plan → onboardingDetails
// .selectedPlan → flatten to onboardingSelectedPlan → shouldShowSubscribeStep.

const PLAN_INTENT_KEY = 'charitystack_signup_plan_intent';

// The tiers the marketing site can forward as a paid intent. STARTER (free) is a
// valid value but carries no paid intent, so it is normalized to null (no stored
// intent → no subscribe step). PRO/ENTERPRISE are sales-assisted (never a self-
// serve ?plan=), so they are not accepted here either. Mirrors the backend's
// self-serve set + the FE shouldShowSubscribeStep SELF_SERVE_SUBSCRIBE_TIERS.
const ACCEPTED_INTENTS = new Set(['BASE', 'PLUS']);

function normalizePlan(raw) {
  if (typeof raw !== 'string') return null;
  const upper = raw.trim().toUpperCase();
  return ACCEPTED_INTENTS.has(upper) ? upper : null;
}

/**
 * Read `?plan=` from the current URL and, if it is a self-serve paid tier,
 * persist it for later consumption by the create-org payload. Idempotent and
 * safe to call on every public-auth-page mount. A non-paid / absent / garbage value
 * is ignored (we do NOT clear an existing stored intent on a param-less reload,
 * so a refresh of /onboarding doesn't wipe a captured choice).
 *
 * @returns {'BASE'|'PLUS'|null} the captured intent, or null if none.
 */
export function capturePlanIntentFromUrl() {
  if (typeof window === 'undefined') return null;
  let plan = null;
  try {
    plan = new URLSearchParams(window.location.search).get('plan');
  } catch {
    return null;
  }
  const intent = normalizePlan(plan);
  if (!intent) return null;
  try {
    localStorage.setItem(PLAN_INTENT_KEY, intent);
  } catch {
    /* storage unavailable (private mode / quota) — non-fatal, intent just won't persist */
  }
  return intent;
}

/**
 * Read the stored plan intent (without clearing it). Returns 'BASE' | 'PLUS' |
 * null. Used by buildCreateOrgPayload to stamp `selectedPlan` on create-org.
 */
export function readPlanIntent() {
  if (typeof window === 'undefined') return null;
  try {
    return normalizePlan(localStorage.getItem(PLAN_INTENT_KEY));
  } catch {
    return null;
  }
}

/**
 * Clear the stored intent — call after it has been sent to create-org so a later
 * unrelated org creation (or a returning user) doesn't re-apply a stale choice.
 */
export function clearPlanIntent() {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(PLAN_INTENT_KEY);
  } catch {
    /* non-fatal */
  }
}
